/**
 * SEO: Meta, TikTok, Google Ads, competitor pricing — plain language, keywords in titles and SEO fields.
 */

export const SOLUTION_CHANNELS = {
  meta: {
    path: "/solutions/meta-ads",
    seo: {
      title: "Meta & Instagram Competitor Monitoring for Shopify | Radar",
      description:
        "Track competitor Facebook ads and Instagram ads for your Shopify store. See new ads, long-running ads, and alerts for brands you choose. Uses public Meta ad data.",
      canonical: "/solutions/meta-ads",
      keywords: [
        "Meta competitor ads",
        "Facebook competitor ads Shopify",
        "Instagram ad monitoring",
        "Meta Ads Library alternative",
      ],
    },
    badge: "Meta & Instagram",
    h1: "Track competitor Meta ads for Shopify",
    heroPunch: "Facebook ads and Instagram ads in one place for the brands you pick.",
    heroSub:
      "Radar shows what is running now, what is new, and what has been up for a long time. You still write your own ads. You spend less time searching the Meta Ads Library by hand.",
    problemTitle: "Limits of only using the Meta Ads Library",
    problemSubtitle: "It is free and public, but it is not built for weekly work.",
    problemPoints: [
      "You see today’s ads, not a simple history of what changed.",
      "You do not get an email when a competitor changes an ad or a code.",
      "With many brands, you copy and paste more than you want to.",
    ],
    featuresTitle: "What Radar does for Meta ads",
    featuresSubtitle: "Adds lists, time, and alerts on top of public ad data.",
    features: [
      {
        title: "Where ads show",
        body: "Feed, stories, reels. You see competitor Facebook and Instagram ads as public data allows, not one cropped image.",
      },
      {
        title: "New ads",
        body: "When a tracked brand runs new creative, you can know without checking the Library yourself every day.",
      },
      {
        title: "Ads that stay up",
        body: "Ads that run a long time often mean the offer worked. Radar helps you notice that pattern.",
      },
      {
        title: "For Shopify stores",
        body: "Built for people who sell on Shopify and need competitor Meta ad data in their normal workflow.",
      },
    ],
    audienceTitle: "Who uses this",
    audience: [
      {
        title: "Store owners and growth",
        body: "You need a fast answer when someone asks what competitors run on Facebook or Instagram.",
      },
      {
        title: "Media buyers",
        body: "One list of competitor Meta ads that updates instead of a new export every time.",
      },
      {
        title: "Agencies",
        body: "Same competitor Meta ad tracking for each client in one tool.",
      },
    ],
    faqs: [
      {
        q: "Does Radar replace the Meta Ads Library?",
        a: "No. The Library is the official list of active ads. Radar helps you follow brands you care about, see changes over time, and use alerts. You still use the Library when you need to.",
      },
      {
        q: "Which Facebook and Instagram ad types do you cover?",
        a: "We use public Meta ad data for Facebook and Instagram. What you see depends on what Meta shows for each ad and brand.",
      },
      {
        q: "Do I need Shopify?",
        a: "Radar is built for Shopify merchants. If you do not use Shopify, this product may not fit.",
      },
    ],
    finalCtaTitle: "Try Radar for competitor Meta ads",
    finalCtaSub: "Free trial. Cancel if it does not help.",
  },
  tiktok: {
    path: "/solutions/tiktok-ads",
    seo: {
      title: "TikTok Competitor Ad Monitoring for Shopify Brands | Radar",
      description:
        "Monitor competitor TikTok ads for your Shopify business. Track brands you choose, see new TikTok ads, and reduce manual scrolling and screenshots.",
      canonical: "/solutions/tiktok-ads",
      keywords: [
        "TikTok competitor ads",
        "spy TikTok ads Shopify",
        "TikTok ad intelligence",
        "competitor TikTok creative monitoring",
      ],
    },
    badge: "TikTok",
    h1: "Track competitor TikTok ads for Shopify",
    heroPunch: "One list of competitor TikTok ads for the brands you select.",
    heroSub:
      "Radar shows new TikTok ads and repeat ideas from competitors you track. Your team can plan video ads with the same facts instead of different saved clips.",
    problemTitle: "Problems with only saving TikTok ads by hand",
    problemSubtitle: "Saved videos do not update for the whole team.",
    problemPoints: [
      "New TikTok ads appear often. Easy to miss a change if you only check sometimes.",
      "Sales and creative may not look at the same saved ads.",
      "Memory is not a written plan for your next TikTok ad.",
    ],
    featuresTitle: "What Radar does for TikTok ads",
    featuresSubtitle: "Track competitor TikTok ads in one place.",
    features: [
      {
        title: "Brands you choose",
        body: "Add the competitors that sell in your space. Skip brands that do not matter to you.",
      },
      {
        title: "New TikTok ads",
        body: "See when a tracked competitor posts new TikTok ad creative.",
      },
      {
        title: "Repeat ideas",
        body: "When the same offer or hook shows up again, you can spot it across weeks.",
      },
      {
        title: "For Shopify sellers",
        body: "For stores on Shopify that run or study TikTok ads, not for general video editing.",
      },
    ],
    audienceTitle: "Who uses this",
    audience: [
      {
        title: "Growth and paid social",
        body: "TikTok ad spend is up and you need a clear view of competitor TikTok ads.",
      },
      {
        title: "Brand and creative",
        body: "You need a steady list of competitor TikTok ads for your next creative plan.",
      },
      {
        title: "Multi-channel brands",
        body: "Use TikTok ad tracking next to Meta and Google ad tracking in Radar if you want one login.",
      },
    ],
    faqs: [
      {
        q: "Can I track any brand on TikTok?",
        a: "You add brands. We show what the product and data sources support for public TikTok ads. If data is missing, we do not hide that.",
      },
      {
        q: "Why not only use save inside TikTok?",
        a: "Save works for one person and one day. Radar is shared, lasts longer, and is built for competitor TikTok ad tracking across a team.",
      },
      {
        q: "Does it connect to Shopify?",
        a: "Radar is for Shopify merchants and teams that work with them. Details are in the product.",
      },
    ],
    finalCtaTitle: "Try Radar for competitor TikTok ads",
    finalCtaSub: "Free trial. Cancel if you do not use it.",
  },
  google: {
    path: "/solutions/google-ads",
    seo: {
      title: "Google Ads Competitor Monitoring for E-Commerce | Radar",
      description:
        "Monitor competitor Google Ads for your Shopify store: Search, Shopping, and other Google ad types where public data is available. Not competitor bid amounts.",
      canonical: "/solutions/google-ads",
      keywords: [
        "Google Ads competitor analysis",
        "Shopping ads competitor monitoring",
        "Google competitor ads ecommerce",
      ],
    },
    badge: "Google Ads",
    h1: "Track competitor Google Ads for Shopify",
    heroPunch: "We do not show competitor bids. Google does not publish that. We help with Google Ads you can see.",
    heroSub:
      "Radar tracks competitor Google Ads activity for brands you choose, over time, where public data allows. Good for planning. Not a copy of Google Ads inside your account.",
    problemTitle: "Why Google Ads competitor data is hard",
    problemSubtitle: "One screen grab is already old.",
    problemPoints: [
      "Auction Insights and exports show one moment, not weeks of change.",
      "Shopping and Performance Max campaigns can be hard to compare by hand.",
      "Sales and finance often use different old files.",
    ],
    featuresTitle: "What Radar does for Google Ads",
    featuresSubtitle: "Only what is visible. No fake bid data.",
    features: [
      {
        title: "Competitors you choose",
        body: "Track the Google Ads competitors that matter to your sales, not every advertiser online.",
      },
      {
        title: "Change over time",
        body: "See how competitor Google Ads and offers shift across weeks and seasons.",
      },
      {
        title: "Next to Meta and TikTok",
        body: "View competitor Google Ads near competitor Meta ads and TikTok ads in Radar if you use those pages.",
      },
      {
        title: "For people who manage budget",
        body: "Less time on slides. More time on clear competitor Google Ads notes.",
      },
    ],
    audienceTitle: "Who uses this",
    audience: [
      {
        title: "Paid search leads",
        body: "The team agrees on competitor Google Ads facts before changing bids.",
      },
      {
        title: "Ops and finance",
        body: "See if competitors push harder on Search or Shopping without a paid report each month.",
      },
      {
        title: "Brands on many channels",
        body: "Google Ads competitor tracking in the same app as other channels.",
      },
    ],
    faqs: [
      {
        q: "Do you show Quality Score or exact cost per click for competitors?",
        a: "No. Google does not give that for other advertisers. We only use public competitor Google Ads information.",
      },
      {
        q: "Which Google Ads formats are included?",
        a: "Depends on Radar and what is public for each brand. Check the live product for today’s coverage.",
      },
      {
        q: "We only use Google Ads. Is that fine?",
        a: "Yes. You can add other channels later or not.",
      },
    ],
    finalCtaTitle: "Try Radar for competitor Google Ads",
    finalCtaSub: "Free trial. Stop if it does not add value.",
  },
  pricing: {
    path: "/solutions/competitor-pricing",
    seo: {
      title: "Competitor Price Monitoring for Shopify Stores | Radar",
      description:
        "Monitor competitor pricing and promotions on their store. Works with the same brands you track for ads. Always confirm big changes on their live site.",
      canonical: "/solutions/competitor-pricing",
      keywords: [
        "competitor price monitoring Shopify",
        "track competitor pricing ecommerce",
        "competitor price alerts",
      ],
    },
    badge: "Pricing & storefront",
    h1: "Competitor price monitoring for Shopify",
    heroPunch: "Same brands as your ad tracking. See price and promotion changes too.",
    heroSub:
      "Radar can alert you when competitor prices or offers change on sites we monitor. For any big decision, open their store and check the live price yourself.",
    problemTitle: "Why manual price checks fail",
    problemSubtitle: "Prices change faster than most spreadsheets.",
    problemPoints: [
      "A sheet you fill on Monday can be wrong on Wednesday.",
      "Sales and codes change without your calendar.",
      "Ads and price often change together. Looking at only ads or only price misses part of the picture.",
    ],
    featuresTitle: "What Radar does for competitor pricing",
    featuresSubtitle: "Alerts and history for competitor price and offers.",
    features: [
      {
        title: "Same brands as ads",
        body: "Track competitor pricing for brands you already follow for Facebook, Instagram, TikTok, or Google ads.",
      },
      {
        title: "Promotions and offers",
        body: "Get notice when a competitor changes how they sell on the site we monitor.",
      },
      {
        title: "Alerts you set",
        body: "You choose what counts as an important competitor price or offer change.",
      },
      {
        title: "For online stores",
        body: "Built for Shopify merchants in categories where competitor pricing moves often.",
      },
    ],
    audienceTitle: "Who uses this",
    audience: [
      {
        title: "Merchandising and e-commerce",
        body: "You need to see competitor discount and price moves before they hurt your sales.",
      },
      {
        title: "Small teams",
        body: "No full-time pricing role. You still need competitor price checks without opening many tabs each morning.",
      },
      {
        title: "Agencies",
        body: "Show clients competitor pricing updates in one place.",
      },
    ],
    faqs: [
      {
        q: "Is every competitor price always exact?",
        a: "We show data from sites we monitor. Before you change your own price, open the competitor site and confirm.",
      },
      {
        q: "Do you track Amazon prices?",
        a: "Radar focuses on Shopify and direct competitor stores. Other sites depend on what we support. Ask us or check the app.",
      },
      {
        q: "Why not only track ads?",
        a: "Sometimes price changes before ads, or ads change before price. Both help you see the full competitor move.",
      },
    ],
    finalCtaTitle: "Try Radar for competitor pricing",
    finalCtaSub: "Free trial. Cancel any time.",
  },
};

export const SOLUTION_ORDER = ["meta", "tiktok", "google", "pricing"];
